import React, { useState, useEffect, useMemo } from 'react';
import { TEnvironment } from '../Types/channel.types';
import SidePanel from 'Components/DesignSystem/SidePanel/SidePanel';
import EnvironmentBasicInfoTab from './EnvironmentBasicInfoTab';
import EnvironmentCommentTab from './EnvironmentCommendTab';
import EnvironmentGuideTab from './EnvironmentGuideTab';
import { SidePanelTab } from '../../../../types/DesignSystem/SidePanel';
import ServiceWrapper from 'Services/ServiceWrapper';
import AuthManager from 'Common/AuthManager';
import { toast } from 'react-toastify';
import AppSettings from 'Settings/AppSettings';
import { EnvironmentAPIResponse, EnvironmentBasicInfoFormData, EnvironmentCommendFormData, EnvironmentGuideFormData } from '../Types/env.types';
import { ConfirmationDialog } from 'Pages/GMS/ISR/UI-Model/components/Common/ConfirmationDialog';

interface EnvironmentSidePanelProps {
  isOpen: boolean;
  onClose: () => void;
  editData?: TEnvironment | null;
  onSave?: (data: EnvironmentAPIResponse) => Promise<void> | void;
  isLoading?: boolean;
  selectedEnvironmentId: string;
  selectedEnvironmentName: string;
  allEnvironmentList: string[];
  hasProductionEnv: boolean;
}

const api_base_url = AppSettings.GMS_BACKEND_API_URL;

const EnvironmentSidePanel: React.FC<EnvironmentSidePanelProps> = ({
  isOpen,
  onClose,
  editData,
  onSave,
  isLoading = false,
  selectedEnvironmentId,
  selectedEnvironmentName,
  allEnvironmentList,
  hasProductionEnv
}) => {
  const [activeTab, setActiveTab] = useState<SidePanelTab>('basic');
  const [isFetchingChannel, setIsFetchingChannel] = useState(false);
  const [showUnSavedChangesDialog, setShowUnSavedChangesDialog] = useState(false);
  // const [showProdConfirmDialog, setShowProdConfirmDialog] = useState(false);
  const [showDisableConfirmDialog, setShowDisableConfirmDialog] = useState(false);
  const [showDisableGuideDialog, setShowDisableGuideDialog] = useState(false);

  // Original snapshot - never changes after API fetch
  const [originalData, setOriginalData] = useState<{
    basic_info: EnvironmentBasicInfoFormData | null;
    commend: EnvironmentCommendFormData | null;
    guide: EnvironmentGuideFormData | null;
  }>({
    basic_info: null,
    commend: null,
    guide: null,
  });

  // Working copies - user edits these
  const [formData, setFormData] = useState<EnvironmentBasicInfoFormData>({
    authentication_token: '',
    carousel_display_settings_desktop: 1,
    carousel_display_settings_mobile: 1,
    carousel_display_settings_tablet: 1,
    channel_key: '',
    enable_compatio_apps: false,
    is_prod: false,
    merchant_ecommerce_domain_url: '',
    name: '',
    platform: '',
    storefront_api_base_url: '',
    third_party_price_engine_integration: 'No',
    webhook_url: '',
  });

  const [commendFormData, setCommendFormData] = useState<EnvironmentCommendFormData>({
    ars_applet_1: 'Yes',
    ars_applet_2: 'Yes',
    ars_applet_3: 'Yes',
    enable_ars: false,
    enable_xrs: false,
    xrs_ars_js_script_url: '',
    xrs_maximum_categories: 5,
    xrs_maximum_products: 50,
    xrs_use_default_value: false,
  });

  const [guideFormData, setGuideFormData] = useState<EnvironmentGuideFormData>({
    enable_guide: false,
    guide_builder_navigation: 'Yes',
    guide_builder_navigation_use_default: false,
    guide_js_script_url: '',
    guide_menu_navigation_name: '',
    guide_menu_navigation_name_use_default: false,
  });

  useEffect(() => {
    if (isOpen && selectedEnvironmentId) {
      fetchChannelData();
    }
  }, [isOpen, selectedEnvironmentId]);

  const fetchChannelData = async () => {
    setIsFetchingChannel(true);
    try {
      const response = await ServiceWrapper.doGet(
        api_base_url + `/channel/${selectedEnvironmentId}`,
        null,
        null,
        AuthManager.getGmsToken()
      );

      if (!response.errorfound && response.data && response.data.data) {
        const apiData = response.data.data as EnvironmentAPIResponse;

        // Ensure all fields exist with defaults for missing API fields
        const basicInfo: EnvironmentBasicInfoFormData = {
          authentication_token: apiData.basic_info.authentication_token || '',
          carousel_display_settings_desktop: apiData.basic_info.carousel_display_settings_desktop || 1,
          carousel_display_settings_mobile: apiData.basic_info.carousel_display_settings_mobile || 1,
          carousel_display_settings_tablet: apiData.basic_info.carousel_display_settings_tablet || 1,
          channel_key: apiData.basic_info.channel_key || '',
          enable_compatio_apps: apiData.basic_info.enable_compatio_apps || false,
          is_prod: apiData.basic_info.is_prod || false,
          merchant_ecommerce_domain_url: apiData.basic_info.merchant_ecommerce_domain_url || '',
          name: apiData.basic_info.name || '',
          platform: apiData.basic_info.platform || '',
          storefront_api_base_url: apiData.basic_info.storefront_api_base_url || '',
          third_party_price_engine_integration: apiData.basic_info.third_party_price_engine_integration || 'No',
          webhook_url: apiData.basic_info.webhook_url || '',
        };

        const commendInfo: EnvironmentCommendFormData = {
          ars_applet_1: apiData.commend.ars_applet_1 || 'Yes',
          ars_applet_2: apiData.commend.ars_applet_2 || 'Yes',
          ars_applet_3: apiData.commend.ars_applet_3 || 'Yes',
          enable_ars: apiData.commend.enable_ars || false,
          enable_xrs: apiData.commend.enable_xrs || false,
          xrs_ars_js_script_url: apiData.commend.xrs_ars_js_script_url || '',
          xrs_maximum_categories: apiData.commend.xrs_maximum_categories || 5,
          xrs_maximum_products: apiData.commend.xrs_maximum_products || 50,
          xrs_use_default_value: apiData.commend.xrs_use_default_value || false,
        };

        const guideInfo: EnvironmentGuideFormData = {
          enable_guide: apiData.guide.enable_guide || false,
          guide_builder_navigation: apiData.guide.guide_builder_navigation || 'Yes',
          guide_js_script_url: apiData.guide.guide_js_script_url || '',
          guide_builder_navigation_use_default: apiData.guide.guide_builder_navigation_use_default || false,
          guide_menu_navigation_name: apiData.guide.guide_menu_navigation_name || '',
          guide_menu_navigation_name_use_default: apiData.guide.guide_menu_navigation_name_use_default || false,
        };

        // Store original snapshot (immutable reference)
        setOriginalData({
          basic_info: JSON.parse(JSON.stringify(basicInfo)),
          commend: JSON.parse(JSON.stringify(commendInfo)),
          guide: JSON.parse(JSON.stringify(guideInfo)),
        });

        // Set working copies
        setFormData(basicInfo);
        setCommendFormData(commendInfo);
        setGuideFormData(guideInfo);
      } else {
        toast.error(response.message || "Failed to fetch environment data")
        onClose();
        console.error('Error fetching channel data:', response.errorbj);
      }
    } catch (error) {
      onClose();
      console.error('Failed to fetch environment data:', error);
      toast.error('Failed to fetch environment data');
    } finally {
      setIsFetchingChannel(false);
    }
  };

  const compareObjects = (original: any, current: any, prefix = ''): string[] => {
    if (!original || !current) return [];

    const changes: string[] = [];

    Object.keys(current).forEach((key) => {
      const fieldPath = prefix ? `${prefix}.${key}` : key;
      const originalValue = original[key];
      const currentValue = current[key];

      if (typeof currentValue === 'object' && currentValue !== null && !Array.isArray(currentValue)) {
        changes.push(...compareObjects(originalValue, currentValue, fieldPath));
      } else {
        if (originalValue !== currentValue) {
          changes.push(fieldPath);
        }
      }
    });

    return changes;
  };

  const changedFieldsCount = useMemo(() => {
    if (!originalData.basic_info || !originalData.commend || !originalData.guide) {
      return 0;
    }

    const basicChanges = compareObjects(originalData.basic_info, formData);
    const commendChanges = compareObjects(originalData.commend, commendFormData);
    const guideChanges = compareObjects(originalData.guide, guideFormData);

    return basicChanges.length + commendChanges.length + guideChanges.length;
  }, [originalData, formData, commendFormData, guideFormData]);

  const handleFormChange = (data: EnvironmentBasicInfoFormData) => {
    setFormData(data);
  };

  const handleCommentFormChange = (data: EnvironmentCommendFormData) => {
    setCommendFormData(data);
  };

  const handleGuideFormChange = (data: EnvironmentGuideFormData) => {
    setGuideFormData(data);
  };

  const handleDisableGuideConfirm = () => {
    setGuideFormData((prev) => ({
      ...prev,
      enable_guide: false,
    }));
    setShowDisableGuideDialog(false);
  };

  const handleDisableGuideCancel = () => {
    setShowDisableGuideDialog(false);
  };

  const handleDisableCompatioAppsConfirm = () => {
    setFormData((prev) => ({
      ...prev,
      enable_compatio_apps: false,
    }));
    setShowDisableConfirmDialog(false);
  };

  const handleDisableCompatioAppsCancel = () => {
    setShowDisableConfirmDialog(false);
  };

  // const handleProdEnvironmentConfirm = () => {
  //   setFormData((prev) => ({
  //     ...prev,
  //     is_prod: true,
  //   }));
  //   setShowProdConfirmDialog(false);
  // };

  // const handleProdEnvironmentCancel = () => {
  //   setShowProdConfirmDialog(false);
  // };

  const validateURL = (url: string): boolean => {
    if (!url) return false;
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
      return false;
    }

    try {
      const urlObj = new URL(url);
      if (!urlObj.protocol.startsWith('http')) {
        return false;
      }

      // Check if hostname exists
      if (!urlObj.hostname || urlObj.hostname.trim().length === 0) {
        return false;
      }

      // Check for valid TLD - domain must have at least one character after the dot
      if (!urlObj.hostname.includes('.') || urlObj.hostname.endsWith('.')) {
        return false;
      }

      // Check if domain has content after the last dot (TLD check)
      const parts = urlObj.hostname.split('.');
      if (parts[parts.length - 1].trim().length === 0) {
        return false;
      }

      // Domain validation pattern
      const domainPattern = /^[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
      if (!domainPattern.test(urlObj.hostname)) {
        return false;
      }
      return true;
    } catch {
      return false;
    }
  };

  const validateForm = (): { isValid: boolean; errors: string[] } => {
    const errors: string[] = [];

    // Basic Info Validations
    if (!formData.name || formData.name.trim().length === 0) {
      errors.push('Environment name is required');
    }

    if (formData.name && formData.name.length > 100) {
      errors.push('Environment name must be 100 characters or less');
    }

    if (!formData.platform || formData.platform === 'Please Select') {
      errors.push('Please select a platform');
    }

    if (!formData.merchant_ecommerce_domain_url || !validateURL(formData.merchant_ecommerce_domain_url)) {
      errors.push('Valid merchant ecommerce domain URL is required');
    }

    if (!formData.authentication_token || formData.authentication_token.trim().length === 0) {
      errors.push('Authentication token is required');
    }

    if (formData.authentication_token) {
      const alphanumericPattern = /^[a-zA-Z0-9]+$/;
      if (!alphanumericPattern.test(formData.authentication_token)) {
        errors.push('Authentication token must be alphanumeric');
      }
    }

    if (!formData.storefront_api_base_url || !validateURL(formData.storefront_api_base_url)) {
      errors.push('Valid store front API URL is required');
    }

    if (formData.webhook_url && !validateURL(formData.webhook_url)) {
      errors.push('Webhook URL must be a valid URL');
    }

    // Guide Validations
    if (guideFormData.enable_guide) {
      if (!guideFormData.guide_js_script_url || !validateURL(guideFormData.guide_js_script_url)) {
        errors.push('Guide JS Script URL is required when Guide is enabled');
      }

      if (guideFormData.guide_builder_navigation === 'Yes') {
        if (!guideFormData.guide_menu_navigation_name || guideFormData.guide_menu_navigation_name.trim().length === 0) {
          errors.push('Menu Navigation Name is required when Builder Navigation is "Yes"');
        }

        if (guideFormData.guide_menu_navigation_name && guideFormData.guide_menu_navigation_name.length > 100) {
          errors.push('Menu Navigation Name must be 100 characters or less');
        }
      }
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  };

  const handleSave = async () => {
    try {
      // Validate form
      const validation = validateForm();
      if (!validation.isValid) {
        // Show errors
        toast.error(
          <div>
            <div className="tw-font-semibold tw-mb-2">Please fix the following errors:</div>
            <ul className="tw-list-disc tw-pl-5">
              {validation.errors.map((error, index) => (
                <li key={index}>{index + 1}. {error}</li>
              ))}
            </ul>
          </div>,
          { autoClose: 5000 }
        );
        return;
      }

      const payload: EnvironmentAPIResponse = {
        basic_info: {
          platform: formData.platform,
          enable_compatio_apps: formData.enable_compatio_apps,
          merchant_ecommerce_domain_url: formData.merchant_ecommerce_domain_url,
          carousel_display_settings_desktop: formData.carousel_display_settings_desktop,
          carousel_display_settings_tablet: formData.carousel_display_settings_tablet,
          carousel_display_settings_mobile: formData.carousel_display_settings_mobile,
          third_party_price_engine_integration: formData.third_party_price_engine_integration,
          webhook_url: formData.webhook_url,
          authentication_token: formData.authentication_token,
          name: formData.name,
          is_prod: formData.is_prod,
          storefront_api_base_url: formData.storefront_api_base_url,
          channel_key: formData.channel_key
        },
        commend: {
          xrs_ars_js_script_url: commendFormData.xrs_ars_js_script_url,
          enable_xrs: commendFormData.enable_xrs,
          xrs_maximum_categories: commendFormData.xrs_maximum_categories,
          xrs_maximum_products: commendFormData.xrs_maximum_products,
          xrs_use_default_value: commendFormData.xrs_use_default_value,
          enable_ars: commendFormData.enable_ars,
          ars_applet_1: commendFormData.ars_applet_1,
          ars_applet_2: commendFormData.ars_applet_2,
          ars_applet_3: commendFormData.ars_applet_3,
        },
        guide: {
          guide_js_script_url: guideFormData.guide_js_script_url,
          enable_guide: guideFormData.enable_guide,
          guide_builder_navigation: guideFormData.guide_builder_navigation,
          guide_builder_navigation_use_default: guideFormData.guide_builder_navigation_use_default,
          guide_menu_navigation_name: guideFormData.guide_menu_navigation_name,
          guide_menu_navigation_name_use_default: guideFormData.guide_menu_navigation_name_use_default,
        },
      };

      await onSave?.(payload);
      onClose();
    } catch (error) {
      console.error('Error saving environment:', error);
    }
  };

  const handleConfirmDiscardUnSavedChanges = () => {
    setShowUnSavedChangesDialog(false);
    onClose();
  };

  const handleCancelUnSavedChangesDialog = () => {
    setShowUnSavedChangesDialog(false);
  };

  const handleClose = () => {
    // Check if there are unsaved changes
    if (changedFieldsCount > 0) {
      setShowUnSavedChangesDialog(true);
      return;
    }
    onClose();
  };

  return (
    <>
      <SidePanel
        isOpen={isOpen}
        onClose={handleClose}
        onSave={handleSave}
        title={editData ? 'Edit Environment: ' + editData?.name : 'Add Environment'}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        isLoading={isLoading || isFetchingChannel}
      >
        {activeTab === 'basic' && (
          <EnvironmentBasicInfoTab
            editData={formData}
            onFormChange={handleFormChange}
            existingEnvironmentNames={allEnvironmentList}
            currentEnvironmentName={selectedEnvironmentName}
            onShowDisableDialog={() => setShowDisableConfirmDialog(true)}
            // onShowProdDialog={() => setShowProdConfirmDialog(true)}
            hasProductionEnv={hasProductionEnv}
            isProduction={originalData.basic_info?.is_prod || false}
          />
        )}
        {activeTab === 'commend' && (
          <EnvironmentCommentTab editData={commendFormData} onFormChange={handleCommentFormChange} isCompatioAppsEnabled={formData.enable_compatio_apps} />
        )}
        {activeTab === 'guide' && (
          <EnvironmentGuideTab
            editData={guideFormData}
            onFormChange={handleGuideFormChange}
            isCompatioAppsEnabled={formData.enable_compatio_apps}
            onShowDisableDialog={() => setShowDisableGuideDialog(true)}
          />
        )}
      </SidePanel>
      <ConfirmationDialog
        isOpen={showUnSavedChangesDialog}
        title="Discard changes?"
        message='You have unsaved changes. Discard them?'
        onConfirm={handleConfirmDiscardUnSavedChanges}
        onCancel={handleCancelUnSavedChangesDialog}
        confirmText="Discard"
      />

      <ConfirmationDialog
        isOpen={showDisableConfirmDialog}
        title="Disable Compatio Applications?"
        message={<>
          This will disable all Compatio applications on your e-commerce site.
          <br />
          Widgets will stop displaying to customers.
        </>}
        onConfirm={handleDisableCompatioAppsConfirm}
        onCancel={handleDisableCompatioAppsCancel}
        confirmText='Proceed'
      />

      {/* <ConfirmationDialog
        isOpen={showProdConfirmDialog}
        title="Set as Production Environment?"
        message="This will mark the current environment as Production. Any other environment marked as Production will be changed to Non-Production."
        onConfirm={handleProdEnvironmentConfirm}
        onCancel={handleProdEnvironmentCancel}
        confirmText='Confirm'
      /> */}

      <ConfirmationDialog
        isOpen={showDisableGuideDialog}
        title="Disable Guide"
        message="This will hide all product builders on your e-commerce site. Continue?"
        onConfirm={handleDisableGuideConfirm}
        onCancel={handleDisableGuideCancel}
        confirmText="Continue"
      />
    </>
  );
};

export default EnvironmentSidePanel;